/**
 * Cennik i ceny klienta z Subiekta (NX-416) — klucze `prc.*`. Scalane w słownik przez `extra.ts`; `prcEn` jest typowane
 * względem `prcPl`, więc brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const prcPl = {
  tab: 'Ceny',
  productsTab: 'Cennik',
  title: 'Ceny klienta',
  subtitle: 'Cena netto towaru dla klienta na wybrany dzień: cena indywidualna lub promocja z Subiekta, poziom ceny albo — gdy ich brak — historia sprzedaży.',
  date: 'Dzień ceny',
  search: 'Szukaj towaru…',
  syncedAt: 'Ceny z Subiekta z {date}',
  notSynced: 'Cen z Subiekta jeszcze nie zaimportowano — widać tylko historię sprzedaży.',
  empty: 'Brak cen dla tego klienta: nie ma cen indywidualnych, a w ostatnich 90 dniach nic nie kupował.',
  terms: {
    level: 'Poziom ceny: {name}',
    defaultLevel: 'Poziom ceny: domyślny',
    discount: 'Rabat klienta: {pct}',
    group: 'Grupa cenowa: {name}',
  },
  col: {
    product: 'Towar',
    unit: 'J.m.',
    net: 'Cena netto',
    list: 'Przed rabatem',
    discount: 'Rabat',
    source: 'Źródło',
    validity: 'Obowiązuje',
    customer: 'Klient',
    level: 'Poziom',
    since: 'Od',
    rule: 'Reguła',
    customers: 'Klienci',
    price: 'Cena',
  },
  source: {
    INDIVIDUAL: 'Cena indywidualna',
    PRICE_LEVEL: 'Poziom ceny',
    HISTORY: 'Historia sprzedaży',
  },
  ruleKind: {
    FIXED_PRICE: 'cena wpisana',
    LEVEL_PERCENT: '{value}% od {level}',
    LEVEL_AMOUNT: '{value} zł do {level}',
  },
  scope: {
    customer: { ALL: 'wszyscy klienci', LISTED: 'klient z listy', GROUP: 'grupa klientów' },
    product: { ALL: 'wszystkie towary', LISTED: 'towar z listy', GROUP: 'grupa towarów' },
  },
  history: { LAST_SALE: 'ostatnia sprzedaż', MEDIAN_30D: 'mediana 30 dni' },
  approximate: 'przybliżona',
  approximateHint: 'Cena sprzed początku historii cen w NEXOPS albo z historii sprzedaży — orientacyjna.',
  unitMismatch: 'inna jednostka',
  from: 'od {date}',
  range: '{from} – {to}',
  open: 'bezterminowo',
  product: {
    pick: 'Wybierz towar, aby zobaczyć jego ceny.',
    noResults: 'Nie znaleziono towaru.',
    levels: 'Poziomy cen',
    levelsEmpty: 'Towar nie ma cen na poziomach.',
    default: 'domyślny',
    customers: 'Ceny indywidualne klientów',
    customersHint: 'Klienci z Twojego zakresu, którzy mają cenę towaru wpisaną imiennie.',
    customersEmpty: 'Żaden klient z Twojego zakresu nie ma ceny indywidualnej tego towaru.',
    groups: 'Reguły dla grup klientów',
    groupsHint: 'Narzuty i ceny dla grup lub wszystkich klientów obowiązujące dla tego towaru.',
    groupsEmpty: 'Brak reguł grupowych dla tego towaru.',
    allCustomers: 'wszyscy klienci',
  },
};

export const prcEn: Shape<typeof prcPl> = {
  tab: 'Prices',
  productsTab: 'Price list',
  title: 'Customer prices',
  subtitle: 'Net price of a product for the customer on the chosen day: individual price or promotion from Subiekt, price level or — when neither exists — sales history.',
  date: 'Price day',
  search: 'Search product…',
  syncedAt: 'Prices from Subiekt as of {date}',
  notSynced: 'Prices from Subiekt have not been imported yet — only the sales history is shown.',
  empty: 'No prices for this customer: no individual prices and nothing bought in the last 90 days.',
  terms: {
    level: 'Price level: {name}',
    defaultLevel: 'Price level: default',
    discount: 'Customer discount: {pct}',
    group: 'Price group: {name}',
  },
  col: {
    product: 'Product',
    unit: 'Unit',
    net: 'Net price',
    list: 'Before discount',
    discount: 'Discount',
    source: 'Source',
    validity: 'Valid',
    customer: 'Customer',
    level: 'Level',
    since: 'Since',
    rule: 'Rule',
    customers: 'Customers',
    price: 'Price',
  },
  source: {
    INDIVIDUAL: 'Individual price',
    PRICE_LEVEL: 'Price level',
    HISTORY: 'Sales history',
  },
  ruleKind: {
    FIXED_PRICE: 'fixed price',
    LEVEL_PERCENT: '{value}% of {level}',
    LEVEL_AMOUNT: '{value} PLN on {level}',
  },
  scope: {
    customer: { ALL: 'all customers', LISTED: 'listed customer', GROUP: 'customer group' },
    product: { ALL: 'all products', LISTED: 'listed product', GROUP: 'product group' },
  },
  history: { LAST_SALE: 'last sale', MEDIAN_30D: '30-day median' },
  approximate: 'approximate',
  approximateHint: 'Price from before the start of the price history in NEXOPS or from the sales history — indicative.',
  unitMismatch: 'different unit',
  from: 'from {date}',
  range: '{from} – {to}',
  open: 'open-ended',
  product: {
    pick: 'Choose a product to see its prices.',
    noResults: 'No product found.',
    levels: 'Price levels',
    levelsEmpty: 'The product has no level prices.',
    default: 'default',
    customers: 'Individual customer prices',
    customersHint: 'Customers in your scope with a price of this product set by name.',
    customersEmpty: 'No customer in your scope has an individual price for this product.',
    groups: 'Customer group rules',
    groupsHint: 'Mark-ups and prices for groups or all customers that apply to this product.',
    groupsEmpty: 'No group rules for this product.',
    allCustomers: 'all customers',
  },
};
